import { useCallback, useMemo, useRef, useState } from 'react'
import { StudioSceneState } from '../types'
import { sceneStatesEqual, serializeSceneState } from '../utils/sceneState'

const HISTORY_CAP = 80

function cloneState(state: StudioSceneState): StudioSceneState {
  return JSON.parse(serializeSceneState(state)) as StudioSceneState
}

export interface UseStudioSceneHistoryApi {
  canUndo: boolean
  canRedo: boolean
  /** Seed / replace the stack (load). Does not count as a user edit. */
  reset: (state: StudioSceneState) => void
  /** Push a snapshot if it differs from the current cursor. Truncates redo. */
  commit: (state: StudioSceneState) => void
  undo: () => StudioSceneState | null
  redo: () => StudioSceneState | null
}

/**
 * In-memory memento stack over `StudioSceneState` (room + arts + pins).
 * Session-only — the saved note is the durable checkpoint. Same idea as
 * meshdom's `patternHistory`, but React owns the document (not Babylon).
 */
export function useStudioSceneHistory(initial: StudioSceneState): UseStudioSceneHistoryApi {
  const stackRef = useRef<StudioSceneState[]>([cloneState(initial)])
  const indexRef = useRef(0)
  const [epoch, setEpoch] = useState(0)
  const bump = useCallback(() => setEpoch((e) => e + 1), [])

  const reset = useCallback(
    (state: StudioSceneState) => {
      stackRef.current = [cloneState(state)]
      indexRef.current = 0
      bump()
    },
    [bump],
  )

  const commit = useCallback(
    (state: StudioSceneState) => {
      const stack = stackRef.current
      const idx = indexRef.current
      const current = stack[idx]
      if (current && sceneStatesEqual(current, state)) return
      const next = stack.slice(0, idx + 1)
      next.push(cloneState(state))
      while (next.length > HISTORY_CAP) next.shift()
      stackRef.current = next
      indexRef.current = next.length - 1
      bump()
    },
    [bump],
  )

  const undo = useCallback((): StudioSceneState | null => {
    if (indexRef.current <= 0) return null
    indexRef.current -= 1
    bump()
    return cloneState(stackRef.current[indexRef.current])
  }, [bump])

  const redo = useCallback((): StudioSceneState | null => {
    if (indexRef.current >= stackRef.current.length - 1) return null
    indexRef.current += 1
    bump()
    return cloneState(stackRef.current[indexRef.current])
  }, [bump])

  return useMemo(
    () => ({
      canUndo: indexRef.current > 0,
      canRedo: indexRef.current < stackRef.current.length - 1,
      reset,
      commit,
      undo,
      redo,
    }),
    // epoch forces canUndo/canRedo to refresh after stack mutations
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [epoch, reset, commit, undo, redo],
  )
}
